/**
 * Uma venda do historico (checkout proprio ou Guru), como o fornecedor registrou: produto, valor, status, quem comprou,
 * os order bumps e os regulamentos aceitos. Usada na busca de vendas antigas e na conferencia da importacao, onde a
 * venda escolhida vira a prova do pagamento. Nao sabe a marca nem decide nada.
 */
import type { ReactNode } from "react";
import { formatarDataHora, formatarReais } from "../formatos";
import { Selo, type Tom } from "./Basicos";

export type AceiteDaVenda = {
  tipo: "PLANO" | "GERAL";
  como: "CHECKOUT" | "IMPLICITO";
  versao: string | null;
  aceito_em: string | null;
  ip: string | null;
  url: string | null;
  documento: { id: string; titulo: string; versao: string } | null;
};
export type VendaDoHistorico = {
  id: string;
  fonte: "CHECKOUT_PROPRIO" | "GURU";
  referencia: string;
  status: "PAGA" | "ESTORNADA" | "CONTESTADA";
  pago_em: string;
  valor_centavos: number;
  meio: string | null;
  parcelas: number | null;
  produto: string;
  comprador: { cpf: string | null; nome: string | null; email: string | null; telefone: string | null };
  cartao_final?: string | null;
  /** `efeito`: o que o order bump prometeu, pra importacao virar beneficio (spec 013, 2.8). */
  itens: { produto: string; valor_centavos?: number | null; referencia?: string | null; efeito?: "ENTREGA" | "CONDICAO" | "PLANO_EM_DOBRO" | null }[];
  aceites: AceiteDaVenda[];
};

export const FONTE_DA_VENDA: Record<VendaDoHistorico["fonte"], string> = { CHECKOUT_PROPRIO: "Checkout próprio", GURU: "Guru" };
export const STATUS_DA_VENDA: Record<VendaDoHistorico["status"], { rotulo: string; tom: Tom }> = {
  PAGA: { rotulo: "Paga", tom: "sucesso" },
  ESTORNADA: { rotulo: "Estornada", tom: "alerta" },
  CONTESTADA: { rotulo: "Contestada", tom: "erro" },
};
const MEIO: Record<string, string> = { pix: "PIX", cartao: "cartão", boleto: "boleto" };

export function CartaoDeVenda({ venda: v, acao, escolhida, aoAbrirDocumento }: { venda: VendaDoHistorico; acao?: ReactNode; escolhida?: boolean; aoAbrirDocumento?: (id: string) => void }) {
  const st = STATUS_DA_VENDA[v.status];
  const pagamento = [v.meio ? MEIO[v.meio] ?? v.meio : null, v.parcelas && v.parcelas > 1 ? `em ${v.parcelas}x` : null, v.cartao_final ? `final ${v.cartao_final}` : null].filter(Boolean).join(" ");
  const comprador = [v.comprador.nome, v.comprador.cpf ? `CPF ${v.comprador.cpf}` : null, v.comprador.email].filter(Boolean).join(" · ");
  return (
    <article className={`mtc-venda${escolhida ? " mtc-venda--escolhida" : ""}`} aria-label={`Venda ${v.produto}, ${FONTE_DA_VENDA[v.fonte]} ${v.referencia}`}>
      <div className="mtc-venda__topo">
        <div className="mtc-venda__titulo">
          <strong>{v.produto}</strong>
          <span className="mtc-venda__ref"><span>{FONTE_DA_VENDA[v.fonte]}</span> <span className="mtc-mono">{v.referencia}</span></span>
        </div>
        <div className="mtc-venda__valor">
          <span className="mtc-mono">{formatarReais(v.valor_centavos)}</span>
          <Selo tom={st.tom}>{st.rotulo}</Selo>
        </div>
      </div>
      <p className="mtc-venda__linha">Paga em {formatarDataHora(v.pago_em)}{pagamento ? `, ${pagamento}` : ""}</p>
      {comprador && <p className="mtc-venda__linha mtc-texto-2">{comprador}</p>}
      {v.itens.length > 0 && (
        <ul className="mtc-venda__lista" aria-label="Order bumps">
          {v.itens.map((i, n) => <li key={`${i.produto}-${n}`}><span className="mtc-venda__marca">Order bump</span> {i.produto}{i.valor_centavos ? ` (${formatarReais(i.valor_centavos)})` : ""}</li>)}
        </ul>
      )}
      {v.aceites.length > 0 && (
        <ul className="mtc-venda__lista" aria-label="Regulamentos aceitos">
          {v.aceites.map((a, n) => (
            <li key={`${a.tipo}-${n}`}>
              <span className="mtc-venda__marca">{a.tipo === "PLANO" ? "Regulamento do plano" : "Regulamento geral"}{a.versao ? ` v${a.versao}` : ""}</span>{" "}
              <span className="mtc-texto-2">{a.como === "CHECKOUT" ? "aceito no checkout" : "termo congelado na matrícula"}{a.aceito_em ? ` em ${formatarDataHora(a.aceito_em)}` : ""}{a.ip ? `, IP ${a.ip}` : ""}.</span>{" "}
              {a.documento ? (
                aoAbrirDocumento ? <button type="button" className="mtc-link" onClick={() => aoAbrirDocumento(a.documento!.id)}>Abrir o documento</button> : null
              ) : (
                <span className="mtc-texto-2">Sem cópia desta versão{a.url ? <>: <a className="mtc-link" href={a.url} target="_blank" rel="noreferrer">endereço de então</a></> : "."}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {acao && <div className="mtc-venda__acao">{acao}</div>}
    </article>
  );
}
